import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, FileText, Download, RefreshCw, Search, Filter,
  CheckCircle, Clock, AlertCircle, FolderOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const colors = {
    INCOMPLETE: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    READY_FOR_OPINION: 'bg-green-100 text-green-800 border-green-200',
    OPENED_BY_ADVOCATE: 'bg-blue-100 text-blue-800 border-blue-200',
    COMPLETED: 'bg-gray-100 text-gray-800 border-gray-200'
  };
  
  const labels = {
    INCOMPLETE: 'Incomplete',
    READY_FOR_OPINION: 'Ready for Opinion',
    OPENED_BY_ADVOCATE: 'In Progress',
    COMPLETED: 'Completed'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${colors[status]}`}>
      {labels[status] || status}
    </span>
  );
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses', icon: FolderOpen },
  { value: 'INCOMPLETE', label: 'Incomplete', icon: AlertCircle },
  { value: 'READY_FOR_OPINION', label: 'Ready for Opinion', icon: Clock },
  { value: 'OPENED_BY_ADVOCATE', label: 'In Progress', icon: FileText },
  { value: 'COMPLETED', label: 'Completed', icon: CheckCircle }
];

export default function CaseHistoryPage() {
  const navigate = useNavigate();
  
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCases();
  }, [statusFilter]);

  const fetchCases = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      params.append('limit', '100');
      
      const { data } = await axios.get(`${API_URL}/api/missing-docs/cases?${params.toString()}`);
      setCases(data.cases || []);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
      setError('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadZip = async (caseId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.get(
        `${API_URL}/api/missing-docs/case/${caseId}/download-zip`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${caseId}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download: ' + (err.response?.data?.detail || err.message));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter cases by search term
  const filteredCases = cases.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      c.case_id?.toLowerCase().includes(term) ||
      c.case_name?.toLowerCase().includes(term) ||
      c.property_type?.toLowerCase().includes(term) ||
      c.profile?.toLowerCase().includes(term)
    );
  });

  // Group cases by status for summary
  const statusCounts = cases.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50" data-testid="case-history-page">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Back
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                📋 Case History
              </h1>
            </div>
            <button
              onClick={() => navigate('/missing-docs')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              + New Case
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {STATUS_OPTIONS.map(opt => {
            const count = opt.value ? (statusCounts[opt.value] || 0) : cases.length;
            const Icon = opt.icon;
            const isActive = statusFilter === opt.value;
            
            return (
              <button
                key={opt.value || 'all'}
                onClick={() => setStatusFilter(opt.value)}
                className={`p-4 rounded-lg border transition-all ${
                  isActive 
                    ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className={`text-2xl font-bold ${isActive ? 'text-blue-600' : 'text-gray-900'}`}>
                    {count}
                  </span>
                </div>
                <p className={`text-xs ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                  {opt.label}
                </p>
              </button>
            );
          })}
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by case ID, name, property type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={fetchCases}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <button onClick={fetchCases} className="text-red-600 hover:underline text-sm mt-2">
              Try again
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading cases...</span>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border">
            <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No cases found</p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="text-blue-600 hover:underline text-sm mt-2"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          /* Cases Table */
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Case
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Profile / Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Docs
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Completeness
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCases.map((c) => (
                    <tr 
                      key={c.case_id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/case/${c.case_id}`)}
                    >
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{c.case_name || c.case_id}</p>
                          <p className="text-xs text-gray-500">{c.case_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-gray-900">{c.profile}</p>
                        <p className="text-xs text-gray-500">{c.property_type?.replace('_', ' ')}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-900">{c.uploaded_docs?.length || 0}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className={`h-2 rounded-full ${
                                c.completeness_percent === 100 ? 'bg-green-500' : 
                                c.completeness_percent >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${c.completeness_percent}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{c.completeness_percent}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-gray-600">{formatDate(c.created_at)}</p>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={(e) => handleDownloadZip(c.case_id, e)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Download ZIP"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
              Showing {filteredCases.length} of {cases.length} cases
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
